import React, { useEffect, useState } from "react";
import { View, StyleSheet, Image, Pressable, Platform, Alert } from "react-native";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import ImageUpload from "../../../components/common/general/ImageUpload/ImageUpload";
import {
  useUpdateCourseMutation,
  useUploadCourseThumbnailMutation,
  useGetCourseByIdQuery,
  useReGenerateCourseImageMutation,
} from "../../../graphql/generated/graphql";
import { Sparkle } from "@phosphor-icons/react";
import NewCustomAxioActionModal from "../../settings/UsersTabContainer/NewCustomAxioActionModal";

const courseInfoSchema = z.object({
  title: z.string().min(1, "Full name is required"),
  isDraft: z.boolean().optional(),
  thumbnail: z.string().optional(),
});

type CourseInfoFormData = z.infer<typeof courseInfoSchema>;

enum ModalState {
  CLOSED = "CLOSED",
  COURSE_IMAGE_REGENERATION = "COURSE_IMAGE_REGENERATION",
}

const CourseInfoContainer = ({ courseId }: { courseId: string }) => {
  const [modalState, setModalState] = useState<ModalState>(ModalState.CLOSED);
  const {
    data: courseData,
    loading,
    error,
    refetch,
  } = useGetCourseByIdQuery({
    variables: {
      id: courseId,
    },
  });

  const course = courseData?.getCourseById?.data?.[0];

  const [updateCourse] = useUpdateCourseMutation();
  const [uploadCourseThumbnail] = useUploadCourseThumbnailMutation();
  const [reGenerateCourseImage] = useReGenerateCourseImageMutation();

  const [uploadError, setUploadError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<CourseInfoFormData>({
    resolver: zodResolver(courseInfoSchema),
    defaultValues: {
      title: "",
      isDraft: false,
      thumbnail: "",
    },
  });

  useEffect(() => {
    if (course) {
      reset({
        title: course.name || "",
        isDraft: course.isDraft || false,
        thumbnail: course.thumbnail || "",
      });
    }
  }, [course, reset]);

  const handleReGenerateCourseImage = async () => {
    setModalState(ModalState.COURSE_IMAGE_REGENERATION);
    try {
      await reGenerateCourseImage({ variables: { courseId: course.id } });
      setModalState(ModalState.CLOSED);
      refetch();
    } catch (error) {
      console.error("Error re-generating course image:", error);
    }
  };

  const handleSave = handleSubmit(async (formData) => {
    try {
      const response = await updateCourse({
        variables: {
          id: course.id,
          course: {
            name: formData.title || "",
            isDraft: formData.isDraft || false,
          },
        },
      });

      if (response.data?.updateCourse?.success) {
        if (formData.thumbnail && formData.thumbnail !== course.thumbnail) {
          const base64Data = formData.thumbnail.split(",")[1];
          const contentType = formData.thumbnail.split(";")[0].split(":")[1];
          const size = Math.floor(base64Data.length * (3 / 4));

          await uploadCourseThumbnail({
            variables: {
              input: {
                courseId: course.id,
                image: base64Data,
                contentType,
                size,
              },
            },
          });
        }

        refetch();
        Alert.alert("Success", "Course updated successfully");
        reset(formData);
      }
    } catch (error) {
      console.error("Error saving course:", error);
      Alert.alert("Error", "Failed to save course changes. Please try again.");
    }
  });

  if (loading || !course) {
    return <CustomText text="Loading..." size="l" weight="ultraBold" style={styles.title} />;
  }

  if (error) {
    return <CustomText text="Error loading course" size="l" weight="ultraBold" style={styles.title} />;
  }

  return (
    <View style={styles.container}>
      <View
        style={{
          flexDirection: "column",
          justifyContent: "flex-start",
          maxWidth: 600,
          gap: 40,
          padding: 40,
          borderWidth: 1,
          borderColor: "#E5E7EB",
          borderRadius: 12,
        }}>
        <View style={styles.form}>
          <View style={styles.formGroup}>
            <CustomText text="Course Title" size="s" weight="ultraBold" style={{}} />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="title"
              disabled={true}
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  onChangeText={onChange}
                  placeholder="Course Name"
                  style={{}}
                  error={errors.title?.message}
                  disabled={true}
                />
              )}
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Course Status" size="s" weight="ultraBold" style={{}} />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="isDraft"
              render={({ field: { onChange, value } }) => (
                <View style={{ flexDirection: "row", gap: 12 }}>
                  <CustomButton
                    text="Published"
                    leftIcon={
                      !value ? (
                        <View
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: "#22C55E",
                            marginRight: 8,
                          }}
                        />
                      ) : undefined
                    }
                    styleType="secondary"
                    size="m"
                    style={{
                      borderColor: !value ? "#22C55E" : "#E5E7EB",
                      borderWidth: 2,
                      backgroundColor: "#fff",
                    }}
                    textStyle={{
                      color: !value ? "#111827" : "#6B7280",
                      fontWeight: !value ? "bold" : "normal",
                    }}
                    onPress={() => onChange(false)}
                  />
                  <CustomButton
                    text="Draft"
                    leftIcon={
                      !value ? undefined : (
                        <View
                          style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: "#22C55E", marginRight: 8 }}
                        />
                      )
                    }
                    styleType="secondary"
                    size="m"
                    style={{
                      borderColor: value ? "#22C55E" : "#E5E7EB",
                      borderWidth: 2,
                      backgroundColor: "#fff",
                    }}
                    textStyle={{
                      color: value ? "#111827" : "#6B7280",
                      fontWeight: value ? "bold" : "normal",
                    }}
                    onPress={() => onChange(true)}
                  />
                </View>
              )}
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Course Thumbnail Image" size="s" weight="ultraBold" style={{}} />
            <CustomSpacing type="vertical" size="s" />
            <View style={{ display: "flex", flexDirection: "column" }}>
              <View style={styles.thumbnailImageSection}>
                <Controller
                  control={control}
                  name="thumbnail"
                  render={({ field: { onChange, value } }) => (
                    <ImageUpload
                      value={value}
                      onChange={onChange}
                      containerStyle={{
                        width: 350,
                        height: 230,
                        borderRadius: 12,
                        borderWidth: 1,
                        borderColor: "#E5E7EB",
                        backgroundColor: "#F3F4F6",
                        borderStyle: "solid",
                      }}
                      imageStyle={{
                        borderRadius: 12,
                        overflow: "visible",
                      }}
                      aspectRatio={[16, 9]}
                      allowsEditing={true}
                      onError={(error) => setUploadError(error.message)}
                      placeholderContent={
                        <View style={styles.thumbnailImagePlaceholder}>
                          <CustomText
                            text={
                              course.name
                                ?.split(" ")
                                .map((n) => n[0])
                                .join("") || ""
                            }
                            size="l"
                            weight="ultraBold"
                            style={styles.placeholderText}
                            useTranslationText={false}
                          />
                        </View>
                      }
                    />
                  )}
                />
              </View>
              <CustomSpacing type="vertical" size="s" />
              <CustomButton
                text="Re-Generate"
                styleType="outline"
                size="m"
                onPress={handleReGenerateCourseImage}
                textStyle={{ color: "#4F46E5" }}
                style={{ width: "fit-content", gap: 8 }}
                leftIcon={<Sparkle size={16} weight="bold" color="#4F46E5" />}
                rightIcon={null}
                textProps={{}}
                useTranslationText={true}
              />
              <CustomText text="courseThumbnailImage" size="xs" style={styles.helperText} />
              <CustomText text="JPG or PNG, max 2MB" size="xs" style={styles.helperText} />
              {uploadError && <CustomText text={uploadError} size="xs" style={styles.errorText} />}
            </View>
          </View>
        </View>
        <View style={styles.footer}>
          <CustomButton
            text="Save"
            styleType="primary"
            size="m"
            onPress={handleSave}
            leftIcon={null}
            rightIcon={null}
            style={{
              backgroundColor: !isDirty ? "#E5E7EB" : "#4F46E5",
              opacity: !isDirty ? 0.5 : 1,
            }}
            textStyle={{
              color: !isDirty ? "#6B7280" : "#FFFFFF",
            }}
            disabled={!isDirty}
            textProps={{}}
            useTranslationText={true}
          />
        </View>
      </View>
      {modalState === ModalState.COURSE_IMAGE_REGENERATION && (
        <NewCustomAxioActionModal
          visible={modalState === ModalState.COURSE_IMAGE_REGENERATION}
          modalTexts={{
            title: "Regenerating Course Image",
            ariaLabel: "Regenerating Course Image",
            warningText: "This may take a minute. You can leave the page if you want.",
            actionButtonText: "Close",
          }}
          onClose={() => setModalState(ModalState.CLOSED)}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    backgroundColor: "#fff",
    width: 600,
    marginHorizontal: "auto",
    flexDirection: "column",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  title: {
    fontSize: 24,
    color: "#111827",
  },
  form: {
    flex: 1,
    maxWidth: 600,
  },
  formGroup: {
    width: "auto",
  },
  helperText: {
    color: "#6B7280",
    marginTop: 4,
  },
  errorText: {
    color: "#EF4444",
    marginTop: 4,
  },
  thumbnailImageSection: {
    alignItems: "flex-start",
    position: "relative",
    width: "fit-content",
  },
  thumbnailImagePlaceholder: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F3F4F6",
  },
  placeholderText: {
    color: "#6B7280",
    fontSize: 40,
  },
});

export default CourseInfoContainer;
